// Main-thread client for the engine worker. Every `EngineApi` method returns
// a Promise; the worker is created on first use. If the worker fails to load
// or its Wasm module fails to instantiate, the engine is unavailable: all
// pending and future calls reject, and `engineFailed()` turns true.
//
// A call that traps or panics inside the engine rejects, and the worker is
// replaced: calls still outstanding are resent to the new one. The same
// happens when a running call times out (`timeoutMs`), since the worker is
// busy with a call that cannot be interrupted any other way.
//
// The worker runs calls one at a time in arrival order. Highlighting is
// background work (whole files, often prefetched), so the client sends one
// `highlight` at a time and holds the rest; every other call goes to the
// worker at once and so waits behind at most one highlight. Held highlights
// go in call order, except that an `urgent` one (the open file's) goes
// before the rest, and `prioritize` moves a held one to the front.
import type { EngineApi } from './types';
import type { EngineCall, EngineReply, EngineRequest } from './worker';

type Async<T> = {
  [K in keyof T]: T[K] extends (...a: infer A) => infer R ? (...a: A) => Promise<R> : never;
};

/** Per-call controls; see `engineWith`. */
export interface CallOptions {
  /** Aborting rejects the call with `signal.reason`. A call that has not
   *  started is dropped from the worker's queue; one already running
   *  finishes in the worker and its result is discarded. */
  signal?: AbortSignal;
  /** Rejects the call once it has been running in the worker for this many
   *  milliseconds, and replaces the worker. Time spent queued behind other
   *  calls does not count. */
  timeoutMs?: number;
  /** For a background call (`highlight`): send it before the background
   *  calls already held, as for the file the reader has open. */
  urgent?: boolean;
}

interface Pending {
  request: EngineCall;
  /** Whether the request has been sent to the worker (a held background
   *  call has not). */
  posted: boolean;
  resolve(value: unknown): void;
  reject(error: unknown): void;
  /** Starts the timeout (when the worker starts the call). */
  started(): void;
  /** Stops the timeout (when the call is resent to a new worker). */
  unstarted(): void;
  /** Detaches the abort listener and timer. */
  cleanup(): void;
}

let worker: Worker | null = null;
let failure: Error | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();

/** Methods sent one at a time, behind everything else. */
const BACKGROUND: ReadonlySet<EngineCall['method']> = new Set(['highlight']);
/** Background calls not yet sent, in call order. */
const held: number[] = [];
/** The background call in the worker, if any. */
let backgroundId: number | null = null;

/** Removes a pending call; returns it unless it had already settled. */
function settle(id: number): Pending | undefined {
  const p = pending.get(id);
  if (!p) return undefined;
  pending.delete(id);
  p.cleanup();
  const i = held.indexOf(id);
  if (i >= 0) held.splice(i, 1);
  if (id === backgroundId) {
    backgroundId = null;
    queueMicrotask(sendBackground);
  }
  return p;
}

/**
 * Moves held background calls of `method` with exactly these arguments to
 * the front, so they are sent next: the highlight of a file the reader
 * opens after prefetch queued it.
 */
export function prioritize(method: EngineCall['method'], args: readonly unknown[]): void {
  const matches = held.filter((id) => {
    const r = pending.get(id)?.request;
    return r?.method === method && r.args.length === args.length && r.args.every((a, i) => a === args[i]);
  });
  if (!matches.length) return;
  const rest = held.filter((id) => !matches.includes(id));
  held.splice(0, held.length, ...matches, ...rest);
}

/** Sends the next held background call once none is in the worker. */
function sendBackground(): void {
  while (backgroundId === null && held.length && !failure) {
    const id = held.shift()!;
    const p = pending.get(id);
    if (!p) continue;
    try {
      getWorker().postMessage(p.request satisfies EngineRequest);
    } catch (e) {
      settle(id)?.reject(e);
      continue;
    }
    p.posted = true;
    backgroundId = id;
  }
}

/** True once the engine is unavailable for the rest of the session (the
 *  worker or its Wasm module could not load); every call now rejects. */
export function engineFailed(): boolean {
  return failure !== null;
}

function fail(error: Error): void {
  failure = error;
  worker?.terminate();
  worker = null;
  for (const id of [...pending.keys()]) settle(id)?.reject(error);
}

/** Replaces the worker and resends every outstanding call to the new one. */
function restart(): void {
  worker?.terminate();
  worker = null;
  if (!pending.size) return;
  let w: Worker;
  try {
    w = getWorker();
  } catch (e) {
    fail(e instanceof Error ? e : new Error(String(e)));
    return;
  }
  for (const [id, p] of [...pending]) {
    if (!p.posted) continue;
    p.unstarted();
    try {
      w.postMessage(p.request satisfies EngineRequest);
    } catch (e) {
      settle(id)?.reject(e);
    }
  }
}

function getWorker(): Worker {
  if (worker) return worker;
  const w = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
  w.addEventListener('message', (e: MessageEvent<EngineReply>) => {
    if (w !== worker) return;
    const reply = e.data;
    if ('started' in reply) {
      pending.get(reply.id)?.started();
      return;
    }
    if (!reply.ok && reply.unavailable) {
      fail(new Error(reply.error));
      return;
    }
    const p = settle(reply.id);
    if (reply.ok) p?.resolve(reply.value);
    else p?.reject(new Error(reply.error));
    if (!reply.ok && reply.fatal) restart();
  });
  w.addEventListener('error', (e: ErrorEvent) => {
    e.preventDefault();
    if (w === worker) fail(new Error(`engine worker failed: ${e.message || 'could not load'}`));
  });
  w.addEventListener('messageerror', () => {
    if (w === worker) fail(new Error('engine worker sent an unreadable message'));
  });
  worker = w;
  return w;
}

function call(method: EngineCall['method'], args: unknown[], options: CallOptions = {}): Promise<unknown> {
  if (failure) return Promise.reject(failure);
  const { signal, timeoutMs, urgent } = options;
  if (signal?.aborted) return Promise.reject(signal.reason);
  let w: Worker;
  try {
    w = getWorker();
  } catch (e) {
    fail(e instanceof Error ? e : new Error(String(e)));
    return Promise.reject(failure);
  }
  const id = nextId++;
  const request: EngineCall = timeoutMs === undefined ? { id, method, args } : { id, method, args, notifyStart: true };
  const background = BACKGROUND.has(method);
  return new Promise((resolve, reject) => {
    // Throws (e.g. DataCloneError) before anything is registered. A held
    // call is posted later, and rejects then if it cannot be.
    if (!background) w.postMessage(request satisfies EngineRequest);
    const onAbort = () => {
      const p = settle(id);
      if (!p) return;
      reject(signal!.reason);
      if (p.posted) worker?.postMessage({ id, cancel: true } satisfies EngineRequest);
    };
    let timer: ReturnType<typeof setTimeout> | undefined;
    pending.set(id, {
      request,
      posted: !background,
      resolve,
      reject,
      started: () => {
        if (timeoutMs === undefined || timer !== undefined) return;
        timer = setTimeout(() => {
          if (!settle(id)) return;
          reject(new Error(`engine.${method} timed out after ${timeoutMs} ms`));
          restart();
        }, timeoutMs);
      },
      unstarted: () => {
        clearTimeout(timer);
        timer = undefined;
      },
      cleanup: () => {
        signal?.removeEventListener('abort', onAbort);
        clearTimeout(timer);
      },
    });
    signal?.addEventListener('abort', onAbort, { once: true });
    if (background) {
      if (urgent) held.unshift(id);
      else held.push(id);
      sendBackground();
    }
  });
}

/** The engine, with `options` applied to every call made through it. */
export function engineWith(options: CallOptions): Async<EngineApi> {
  const method =
    <K extends keyof EngineApi>(name: K) =>
    (...args: Parameters<EngineApi[K]>) =>
      call(name, args, options) as Promise<ReturnType<EngineApi[K]>>;
  return {
    languages: method('languages'),
    detectLanguage: method('detectLanguage'),
    highlight: method('highlight'),
    symbols: method('symbols'),
    parsePatch: method('parsePatch'),
    blocks: method('blocks'),
    lineMap: method('lineMap'),
    resolveLegacy: method('resolveLegacy'),
    detectMoves: method('detectMoves'),
    attention: method('attention'),
  };
}

/** The engine, running in a Web Worker. */
export const engine: Async<EngineApi> = engineWith({});

let ready: Promise<void> | null = null;

/** Resolves once the Wasm engine has loaded; rejects if it cannot. */
export function engineReady(): Promise<void> {
  ready ??= call('__ready', []).then(() => undefined);
  return ready;
}
